import { use } from 'react';
import { PassageContext } from '@/lib/context/passage-provider';

/**
 * Custom hook to access passage context with derived state
 *
 * Provides access to computed values based on the passage state,
 * such as the current passage looked up from passageId.
 * Must be used within a PassageProvider.
 *
 * This is the main hook most components should use for passage data.
 * For low-level state access, use usePassageState instead.
 *
 * @returns PassageContextType with derived state (passage)
 * @throws Error if used outside of PassageProvider
 *
 * @example
 * ```tsx
 * import { usePassage } from '@/lib/hooks/use-passage';
 *
 * function MyComponent() {
 *   const { section: passage } = usePassage();
 *
 *   return (
 *     <div>
 *       {passage ? (
 *         <p>{passage.book} {passage.ref}: {passage.title}</p>
 *       ) : (
 *         <p>No passage selected</p>
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
export function usePassage() {
    const context = use(PassageContext);

    if (!context) {
        throw new Error('usePassage must be used within a PassageProvider');
    }

    return context;
}

